import { useEffect, useRef, type ReactNode } from 'react';
import { Navigate, Outlet, useRouterState } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import { Button } from '@org/ui';
import { decideGate, STORE_PATH, VALIDATE_PATH } from '../../lib/access';
import { useAccess } from './use-access';

/**
 * Sits between the Auth0 guard and the student shell. Group / private / b2b
 * students pass straight through. A self-learning student who arrived by
 * themselves goes through, in order:
 *
 *   1. no record yet → `access.bootstrap` creates it from the Google profile
 *      (status 'awaiting-terms'), then the page reloads so the app-wide
 *      `AuthBootstrapper` re-runs `verifyUser` and opens the terms dialog —
 *      it only runs once per login, and that run happened before the record
 *      existed;
 *   2. terms not accepted → a quiet splash under the (modal) terms dialog;
 *   3. identity not validated → the full-page photo check (no sidebar);
 *   4. validated, never enrolled → the store, where the free trial starts;
 *   5. the app. Once the trial is over and nothing was paid, the learning
 *      pages render under the faded `LimitedVeil` (see `_app.tsx`).
 */
export function AccessGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation('p2e');
  const { user } = useAuth0();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const utils = trpc.useUtils();
  const { data, isLoading, isError, refetch } = useAccess();

  const bootstrap = trpc.access.bootstrap.useMutation({
    onSuccess: (next) => {
      utils.access.me.setData(undefined, next);
      // See step 1 above: let the terms dialog pick the new record up.
      window.location.reload();
    },
  });
  const bootstrapped = useRef(false);

  const mode = decideGate(isLoading ? null : data, pathname);

  useEffect(() => {
    if (mode !== 'bootstrap' || bootstrapped.current) return;
    bootstrapped.current = true;
    bootstrap.mutate({
      firstName: user?.given_name ?? undefined,
      lastName: user?.family_name ?? undefined,
      pictureUrl: user?.picture ?? undefined,
    });
    // The Auth0 profile is stable for the session; only `mode` drives this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // While the terms dialog is open the snapshot is polled so the gate moves
  // on by itself the moment the student accepts.
  useEffect(() => {
    if (mode !== 'terms') return;
    const id = window.setInterval(() => void refetch(), 2000);
    return () => window.clearInterval(id);
  }, [mode, refetch]);

  if (isError || bootstrap.isError) {
    return (
      <Splash>
        <p className="mb-3">{t('access.gate.error')}</p>
        <Button
          type="button"
          variant="red"
          onClick={() => {
            bootstrapped.current = false;
            bootstrap.reset();
            void refetch();
          }}
        >
          {t('access.gate.retry')}
        </Button>
      </Splash>
    );
  }

  if (mode === 'loading' || mode === 'bootstrap') {
    return <Splash>{t('access.gate.loading')}</Splash>;
  }

  if (mode === 'terms') {
    return <Splash>{t('access.gate.terms')}</Splash>;
  }

  if (mode === 'validate') {
    // The validation page renders through the outlet without the sidebar.
    if (pathname !== VALIDATE_PATH) return <Navigate to={VALIDATE_PATH} replace />;
    return <Outlet />;
  }

  if (pathname === VALIDATE_PATH) {
    // Already validated: the only way forward from here is the store.
    return <Navigate to={STORE_PATH} replace />;
  }

  if (mode === 'store') {
    return <Navigate to={STORE_PATH} replace />;
  }

  return <>{children}</>;
}

function Splash({ children }: { children: ReactNode }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-screen flex-col items-center justify-center bg-[#fff8fb] px-6 text-center text-[#2D1A1F]"
    >
      {children}
    </div>
  );
}
